import TablePagination from '@amzn/awsui-components-react/polaris/table-pagination';
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableContentSelector,
  TablePreferences,
  TablePropertyFiltering,
  TableSelection,
  TableSorting,
} from 'AWS-UI-Components-React';
import React from 'react';
import Select from 'react-select';
import Creatable from 'react-select/lib/Creatable';
import { downloadFile, toDownloadFormat } from '../../../utilities';
import { getChannelMappingFilters, areFieldValuesEquivalent } from './channel_mapping_utils';
import { CHANNEL_MAPPING } from './channel_mapping_constants';
import { CMReviewChanges } from './cm_review_changes';
import { CMAddMapping } from './cm_add_mapping';
import { CMBulkUpload } from './cm_bulk_upload';
import { V2_PROPERTY_FILTERING_TEXT, v2PropertyFilteringFunction, getFilteredItems } from '../../common/property_filter_helpers';

const tablePageSize = 30;

const hiddenColumns = ['comments', 'ignored'];

export class ChannelMappingTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      reviewChangesScreen: { visible: false, comment: 'Changes by user: ' + this.props.user + ' at ' + new Date().toUTCString() },
      addMappingScreen: { visible: false },
      bulkUploadScreen: { visible: false },
      modifiedChannelMappingsById: {},
      stagedChannelMappings: {},
      latestChannelMappingsById: {},
      latestChannelMappings: [],
      selectedItems: [],
      editingValues: {},
      filteringTokens: [],
      filteringOperation: 'and',
    }
  }

  getColumnDefinitions = () => {
    return [
      {
        id: 'cognosChannelType',
        header: this.formatHeader('Cognos Channel Type'),
        columnName: 'Cognos Channel Type',
        minWidth: '90px',
        cell: (item) => {
          const isNewCognosType = !Object.keys(this.props.originalChannelMappings || {}).some(id => id.startsWith(item.cognosChannelType + '#'));
          const rowIsNew = !this.props.originalChannelMappings[item.id];
          return (
            <span>
              {item.cognosChannelType} {isNewCognosType && !rowIsNew && <Badge color="green">New</Badge>} {this.getBadges(item)}
            </span>
          );
        },
      },
      {
        id: 'channelDescription',
        header: this.formatHeader('Cognos Channel Description'),
        columnName: 'Cognos Channel Description',
        minWidth: '180px',
        cell: (item) => this.renderEditableCell(item, 'channelDescription'),
      },
      {
        id: 'cognosParentChannel',
        header: this.formatHeader('Cognos Parent Channel'),
        columnName: 'Cognos Parent Channel',
        minWidth: '130px',
        cell: (item) => this.renderSelectCell(item, 'cognosParentChannel', (this.props.dropdownOptions || {}).cognosParentChannelOptions),
      },
      {
        id: 'cognosChannel',
        header: this.formatHeader('Cognos Channel'),
        columnName: 'Cognos Channel',
        minWidth: '130px',
        cell: (item) => this.renderSelectCell(item, 'cognosChannel', (this.props.dropdownOptions || {}).cognosChannelOptions),
      },
      {
        id: 'groupCode',
        header: this.formatHeader('Cognos Group Code'),
        columnName: 'Cognos Group Code',
        minWidth: '130px',
        cell: (item) => this.renderSelectCell(item, 'groupCode', (this.props.dropdownOptions || {}).groupCodeOptions),
      },
      {
        id: 'hcpMerchantChannel',
        header: this.formatHeader('HCP Merchant Channel'),
        columnName: 'HCP Merchant Channel',
        minWidth: '180px',
        cell: (item) => this.channelSelectionComponent(item),
      },
      {
        id: 'hcpChannelType',
        header: this.formatHeader('HCP Channel Type'),
        columnName: 'HCP Channel Type',
        minWidth: '130px',
        cell: (item) => this.channelTypeSelectionComponent(item),
      },
      {
        id: 'marketplaceId',
        header: this.formatHeader('Marketplace Id'),
        columnName: 'Marketplace Id',
        minWidth: '100px',
        cell: (item) => this.renderMarketplaceCell(item),
      },
      {
        id: 'derivedMarketplaceId',
        header: this.formatHeader('Derived Marketplace Id'),
        columnName: 'Derived Marketplace Id',
        minWidth: '100px',
        cell: (item) => this.renderDerivedMarketplaceCell(item),
      },
      {
        id: 'marketplaceName',
        header: this.formatHeader('Marketplace Name'),
        columnName: 'Marketplace Name',
        minWidth: '50px',
        cell: (item) => item.marketplaceName || '',
      },
      {
        id: 'excluded',
        header: this.formatHeader('Cognos Excluded'),
        columnName: 'Cognos Excluded',
        minWidth: '50px',
        cell: (item) => (
          <Checkbox
            checked={item.excluded || false}
            controlId={item.id}
            onChange={(event) => this.onExcludedChange(event, item)}
            disabled={this.props.readOnly}
          />
        )
      },
      {
        id: 'hcpExcluded',
        header: this.formatHeader('HCP Excluded'),
        columnName: 'HCP Excluded',
        minWidth: '50px',
        cell: (item) => (
          <Checkbox
            checked={item.hcpExcluded || false}
            controlId={item.id}
            onChange={(event) => this.onHCPExcludedChange(event, item)}
            disabled={this.props.readOnly}
          />
        )
      },
      {
        id: 'ignored',
        header: this.formatHeader('Ignored'),
        columnName: 'Ignored',
        minWidth: '50px',
        cell: (item) => (
          <Checkbox
            checked={item.ignored || false}
            controlId={item.id}
            onChange={(event) => this.onIgnoredChange(event, item)}
            disabled={this.props.readOnly}
          />
        )
      },
      {
        id: 'comments',
        header: this.formatHeader('Comments'),
        columnName: 'Comments',
        cell: (item) => item.comments,
      },
    ];
  };

  renderEditableCell = (item, field) => {
    if (this.props.readOnly) return item[field] || '';
    const editKey = `${item.id}::${field}`;
    const isEditing = this.state.editingValues && editKey in this.state.editingValues;
    const displayValue = isEditing ? this.state.editingValues[editKey] : (item[field] || '');
    return (
      <input
        type="text"
        value={displayValue}
        onChange={(e) => {
          const val = e.target.value;
          this.setState(prev => ({
            editingValues: { ...prev.editingValues, [editKey]: val }
          }));
        }}
        onBlur={() => {
          if (this._suppressBlur || !isEditing) return;
          const newValue = this.state.editingValues[editKey];
          this.setState(prev => {
            const { [editKey]: _, ...rest } = prev.editingValues;
            return { editingValues: rest };
          });
          setTimeout(() => {
            if (!this._suppressBlur) {
              this.onCellBlur(item, field, newValue);
            }
          }, 0);
        }}
        onFocus={() => {
          if (!isEditing) {
            this.setState(prev => ({
              editingValues: { ...prev.editingValues, [editKey]: item[field] || '' }
            }));
          }
        }}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #d5dbdb', borderRadius: '2px' }}
      />
    );
  };

  renderCreatableCell = (item, field, options) => {
    if (this.props.readOnly) return item[field] || '';
    if (!options || !options.length) return this.renderEditableCell(item, field);
    const currentValue = item[field] ? { value: item[field], label: item[field] } : null;
    const isNewValue = item[field] && !options.some(o => o.value === item[field]);
    return (
      <div>
        <Creatable
          options={options}
          value={currentValue}
          onChange={(selected) => {
            if (selected) this.onCellBlur(item, field, selected.value);
          }}
          isClearable={false}
          menuPortalTarget={document.body}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), container: base => ({ ...base, minWidth: 100 }) }}
        />
        {isNewValue && <Badge color="green">New</Badge>}
      </div>
    );
  };

  renderSelectCell = (item, field, options) => {
    if (this.props.readOnly) return item[field] || '';
    if (!options || !options.length) return this.renderEditableCell(item, field);
    const currentValue = item[field] ? { value: item[field], label: item[field] } : null;
    return (
      <Select
        options={options}
        value={currentValue}
        onChange={(selected) => {
          if (selected) this.onCellBlur(item, field, selected.value);
        }}
        isClearable={false}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), container: base => ({ ...base, minWidth: 100 }) }}
      />
    );
  };

  renderMarketplaceCell = (item) => {
    if (this.props.readOnly) return item.marketplaceId || '';
    const options = (this.props.dropdownOptions || {}).marketplaceIdOptions || [];
    if (!options.length) return item.marketplaceId || '';
    const currentValue = item.marketplaceId ? { value: item.marketplaceId, label: `${item.marketplaceId}` } : null;
    return (
      <Select
        options={options}
        value={currentValue}
        onChange={(selected) => {
          if (!selected) return;
          const mpId = selected.value;
          // Auto-resolve derivedMarketplaceId and marketplaceName from marketplaceConfig
          const config = (this.props.marketplaceConfig || []).find(c =>
            c.derivedMarketplaceIds && c.derivedMarketplaceIds.includes(parseInt(mpId))
          );
          const marketplaceName = config ? config.detail : '';
          // If current derivedMarketplaceId is not under new marketplace, default to marketplaceId
          const derivedIds = config ? config.derivedMarketplaceIds : [];
          const derivedMarketplaceId = derivedIds.includes(parseInt(item.derivedMarketplaceId))
            ? item.derivedMarketplaceId : mpId;
          const modifiedMapping = { ...item, marketplaceId: mpId, derivedMarketplaceId, marketplaceName };
          this.stageChannelMappingChanges({ [item.id]: modifiedMapping }, this.props.channelMappingsById);
        }}
        isClearable={false}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), container: base => ({ ...base, minWidth: 100 }) }}
      />
    );
  };

  renderDerivedMarketplaceCell = (item) => {
    if (this.props.readOnly) return item.derivedMarketplaceId || '';
    // Get derived IDs filtered by current marketplaceId
    const config = (this.props.marketplaceConfig || []).find(c =>
      c.derivedMarketplaceIds && c.derivedMarketplaceIds.includes(parseInt(item.marketplaceId))
    );
    const derivedIds = config ? config.derivedMarketplaceIds : [];
    const options = derivedIds.map(id => ({ value: String(id), label: `${id}` }));
    if (!options.length) return item.derivedMarketplaceId || '';
    const val = item.derivedMarketplaceId != null ? String(item.derivedMarketplaceId) : '';
    const currentValue = val ? options.find(o => o.value === val) || { value: val, label: val } : null;
    return (
      <Select
        options={options}
        value={currentValue}
        onChange={(selected) => {
          if (!selected) return;
          this.onCellBlur(item, 'derivedMarketplaceId', selected.value);
        }}
        isClearable={false}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), container: base => ({ ...base, minWidth: 100 }) }}
      />
    );
  };

  onCellBlur = (item, field, newValue) => {
    const originalValue = item[field] || '';
    if (String(newValue).trim() === String(originalValue).trim()) return;

    const serverOriginal = this.props.originalChannelMappings[item.id];
    const modifiedMapping = {
      ...item,
      [field]: String(newValue).trim(),
      comments: `Edited by ${this.props.user} at ${new Date().toUTCString()}`,
    };

    // Key field changed — silently delete old + create new
    if ((field === 'cognosChannelType' || field === 'hcpChannelType') && serverOriginal) {
      const newId = `${modifiedMapping.cognosChannelType}#${modifiedMapping.hcpChannelType}#*`;
      if (newId !== item.id) {
        // Delete old
        const deletedMapping = { ...item, ignored: true, _deleted: true, comments: `Deleted by ${this.props.user} at ${new Date().toUTCString()}` };
        this.stageChannelMappingChanges({ [item.id]: deletedMapping }, this.props.channelMappingsById);
        // Add new
        const newMapping = { ...modifiedMapping, id: newId, version: '0' };
        this.props.addChannelMappings(newMapping);
        return;
      }
    }

    // Auto-unstage if reverting all editable fields to original
    if (serverOriginal && isNaN(item.version)) {
      const editableFields = CHANNEL_MAPPING.EDITABLE_FIELDS;
      const allMatch = editableFields.every(f =>
        areFieldValuesEquivalent(serverOriginal[f], modifiedMapping[f])
      );
      if (allMatch) {
        const toUnstage = { [item.id]: item };
        this.props.unstageChannelMappingChanges(toUnstage, this.props.originalChannelMappings);
        return;
      }
    }

    const modifiedById = { [item.id]: modifiedMapping };
    this.stageChannelMappingChanges(modifiedById, this.props.channelMappingsById);
  };

  onDeleteSelected = () => {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return;

    const modifiedById = {};
    for (const item of selectedItems) {
      modifiedById[item.id] = {
        ...item,
        ignored: true,
        _deleted: true,
        comments: `Deleted by ${this.props.user} at ${new Date().toUTCString()}`,
      };
    }
    this.stageChannelMappingChanges(modifiedById, this.props.channelMappingsById);
    this.setState({ selectedItems: [] });
  };

  onSelectionChange = (event) => {
    this.setState({ selectedItems: event.detail.selectedItems });
  };

  formatHeader = (header) => {
    if (header.includes("Cognos")) {
      return this.formatStyle(header, "green");
    } else if (header.includes("HCP")) {
      return this.formatStyle(header, "crimson");
    } else {
      return header;
    }
  };

  formatStyle = (header, color) => {
    return <span style={{color: color}}> {header} </span>;
  };

  onClickDownloadData = (columnDefinitions, channelMappings) => {
    columnDefinitions.map((def) => def.header = def.columnName);
    const { filteringTokens, filteringOperation } = this.state;
    const data = toDownloadFormat(columnDefinitions, getFilteredItems(channelMappings, filteringTokens, filteringOperation));
    downloadFile(data, 'Channel_Mappings.xls');
  };

  getBadges = (channelMapping) => {
    let badges = [];
    if (isNaN(channelMapping.version)) {
      const isNew = !this.props.originalChannelMappings[channelMapping.id];
      const isDeleted = channelMapping._deleted || (channelMapping.comments && channelMapping.comments.startsWith('Deleted by'));

      const label = isDeleted ? 'Deleted' : isNew ? 'New' : 'Staged';
      const color = isDeleted ? 'red' : isNew ? 'green' : 'blue';
      badges.push(
        <Badge key={`$STAGED_${channelMapping.id}`} color={color}>
          {label}
        </Badge>
      );
    }

    return badges.map((badge) => <span key={badge.key}>{badge} </span>);
  };

  channelSelectionComponent = (channelMapping) => {
    if (!this.props.dropdownOptions || !this.props.dropdownOptions.hcpChannelOptions) return channelMapping.hcpMerchantChannel || '';
    const found = this.props.dropdownOptions.hcpChannelOptions.find((option) => option.value === channelMapping.hcpMerchantChannel);
    const selected = found || (channelMapping.hcpMerchantChannel ? { value: channelMapping.hcpMerchantChannel, label: channelMapping.hcpMerchantChannel } : null);
    return (
      <Select
        options={this.props.dropdownOptions.hcpChannelOptions}
        value={selected}
        onChange={(selectedOption) => this.onChannelSelectionChange(selectedOption, channelMapping)}
        isMulti={false}
        isDisabled={this.props.readOnly}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };

  channelTypeSelectionComponent = (channelMapping) => {
    if (!this.props.dropdownOptions || !this.props.dropdownOptions.hcpChannelTypeOptions) return channelMapping.hcpChannelType || '';
    const found = this.props.dropdownOptions.hcpChannelTypeOptions.find((option) => option.value === channelMapping.hcpChannelType);
    const selected = found || (channelMapping.hcpChannelType ? { value: channelMapping.hcpChannelType, label: channelMapping.hcpChannelType } : null);
    return (
      <Creatable
        options={this.props.dropdownOptions.hcpChannelTypeOptions}
        value={selected}
        onChange={(selectedOption) => this.onChannelTypeSelectionChange(selectedOption, channelMapping)}
        isMulti={false}
        isDisabled={this.props.readOnly}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };

  onChannelTypeSelectionChange = (selectedOption, channelMapping) => {
    const originalChannelMapping = this.getOriginalChannelMapping(channelMapping);
    const modifiedChannelMapping = this.getModifiedChannelMapping(channelMapping);
    const oldHcpChannelType = channelMapping.hcpChannelType;
    const newHcpChannelType = selectedOption.value;

    modifiedChannelMapping.hcpChannelType = newHcpChannelType;
    modifiedChannelMapping.comments = this.state.reviewChangesScreen.comment;

    // Key field changed — silently delete old entry and create new one
    if (oldHcpChannelType !== newHcpChannelType && originalChannelMapping) {
      const oldId = channelMapping.id;
      const newId = `${channelMapping.cognosChannelType}#${newHcpChannelType}#*`;

      // Delete old entry (stage as excluded+ignored)
      const deletedMapping = { ...originalChannelMapping, ignored: true, _deleted: true, comments: `Deleted by ${this.props.user} at ${new Date().toUTCString()}` };
      const deleteById = { [oldId]: deletedMapping };
      this.stageChannelMappingChanges(deleteById, this.props.channelMappingsById);

      // Add new entry with new key — use original excluded/ignored values
      const serverOriginal = this.props.originalChannelMappings[oldId] || originalChannelMapping;
      const newMapping = {
        ...modifiedChannelMapping,
        id: newId,
        version: '0',
        excluded: serverOriginal.excluded || false,
        ignored: serverOriginal.ignored || false,
      };
      delete newMapping.last;
      delete newMapping.timestamp;
      this.props.addChannelMappings(newMapping);

      this.setState({ modifiedChannelMappingsById: {} });
      return;
    }

    this.handleChannelMappingModification(originalChannelMapping, modifiedChannelMapping);
  };

  onChannelSelectionChange = (selectedOption, channelMapping) => {
    const originalChannelMapping = this.getOriginalChannelMapping(channelMapping);
    const modifiedChannelMapping = this.getModifiedChannelMapping(channelMapping);

    modifiedChannelMapping.hcpMerchantChannel = selectedOption.value;
    modifiedChannelMapping.phoenixChannel = selectedOption.value;
    modifiedChannelMapping.comments = this.state.reviewChangesScreen.comment;

    this.handleChannelMappingModification(originalChannelMapping, modifiedChannelMapping);
  };

  getOriginalChannelMapping = (channelMapping) => {
    return this.props.channelMappingsById[channelMapping.id];
  };

  getModifiedChannelMapping = (channelMapping) => {
    const { id } = channelMapping;
    return this.state.modifiedChannelMappingsById[id]
           ? { ...this.state.modifiedChannelMappingsById[id] }
           : { ...channelMapping };
  };

  onExcludedChange = (event, channelMapping) => {
    const originalChannelMapping = this.getOriginalChannelMapping(channelMapping);
    const modifiedChannelMapping = this.getModifiedChannelMapping(channelMapping);
    modifiedChannelMapping.excluded = Boolean(event.detail.checked);
    modifiedChannelMapping.comments = this.state.reviewChangesScreen.comment;
    channelMapping.excluded = Boolean(event.detail.checked);

    this.handleChannelMappingModification(originalChannelMapping, modifiedChannelMapping);
  };

  onHCPExcludedChange = (event, channelMapping) => {
    const originalChannelMapping = this.getOriginalChannelMapping(channelMapping);
    const modifiedChannelMapping = this.getModifiedChannelMapping(channelMapping);
    modifiedChannelMapping.hcpExcluded = Boolean(event.detail.checked);
    modifiedChannelMapping.comments = this.state.reviewChangesScreen.comment;
    channelMapping.hcpExcluded = Boolean(event.detail.checked);

    this.handleChannelMappingModification(originalChannelMapping, modifiedChannelMapping);
  };

  onIgnoredChange = (event, channelMapping) => {
    const originalChannelMapping = this.getOriginalChannelMapping(channelMapping);
    const modifiedChannelMapping = this.getModifiedChannelMapping(channelMapping);
    modifiedChannelMapping.ignored = Boolean(event.detail.checked);
    modifiedChannelMapping.comments = this.state.reviewChangesScreen.comment;
    channelMapping.ignored = Boolean(event.detail.checked);

    this.handleChannelMappingModification(originalChannelMapping, modifiedChannelMapping);
  };

  reviewChangesScreenDismiss = () => {
    this.props.onScreenDismiss();
    this.setState({
                    latestChannelMappings: [],
                    reviewChangesScreen: {
                      visible: false,
                      stagedChannelMappings: null,
                      latestChannelMappingsById: null,
                      successScreenMessage: null,
                      errorScreenMessage: null,
                    },
                  });
    this.props.refreshChannelMapping();
  };

  addMappingScreenDismiss = () => {
    this.props.onScreenDismiss();
    this.setState({
                    addMappingScreen: {
                      visible: false,
                      stagedChannelMappings: null,
                      latestChannelMappingsById: null,
                      successScreenMessage: null,
                      errorScreenMessage: null,
                    },
                  });
  };

  hasStagedChanges = () => {
    const { channelMappingsById, originalChannelMappings } = this.props;
    for (let id of Object.keys(channelMappingsById || {})) {
      const item = channelMappingsById[id];
      if (isNaN(item.version)) {
        // Skip silently-deleted entries only when paired new entry exists
        const original = originalChannelMappings[id];
        if ((item._deleted || (item.comments && item.comments.startsWith('Deleted by'))) && original && (!original.excluded || !original.ignored)) {
          const cognosType = id.split('#')[0];
          const hasReplacement = Object.keys(channelMappingsById).some(otherId =>
            otherId !== id && otherId.startsWith(cognosType + '#') && !originalChannelMappings[otherId]
          );
          if (hasReplacement) continue;
        }
        return true;
      }
    }

    return false;
  };

  getStagedChannelMappings = () => {
    const { channelMappingsById } = this.props;
    let stagedChannelMappings = [];
    for (let id of Object.keys(channelMappingsById || {})) {
      if (isNaN(channelMappingsById[id].version)) {
        stagedChannelMappings.push(channelMappingsById[id]);
      }
    }

    return stagedChannelMappings.reduce((obj, item) => ({...obj, [item['id']]: item}), {});;
  };

  onClickReviewPublishChanges = () => {
    this.props.refreshChannelMapping();
    const { channelMappingsById } = this.props;
    this.setState({
                    reviewChangesScreen: {
                      visible: true,
                      stagedChannelMappings: this.getStagedChannelMappings(),
                      latestChannelMappingsById: channelMappingsById,
                      comment: 'Changes by user: ' + this.props.user + ' at ' + new Date().toUTCString(),
                    },
                  });
  };

  handleChannelMappingModification = (originalChannelMapping, modifiedChannelMapping) => {
    const { id } = originalChannelMapping;
    const serverOriginal = this.props.originalChannelMappings[id];

    // Auto-unstage if all editable fields match the server original
    if (serverOriginal) {
      const editableFields = CHANNEL_MAPPING.EDITABLE_FIELDS;
      const allMatch = editableFields.every(field =>
        areFieldValuesEquivalent(serverOriginal[field], modifiedChannelMapping[field])
      );
      if (allMatch) {
        const toUnstage = { [id]: modifiedChannelMapping };
        this.props.unstageChannelMappingChanges(toUnstage, this.props.originalChannelMappings);
        this.setState({ modifiedChannelMappingsById: {} });
        return;
      }
    }

    let modifiedChannelMappingsById = {
      ...this.state.modifiedChannelMappingsById,
      [id]: modifiedChannelMapping,
    };

    this.stageChannelMappingChanges(modifiedChannelMappingsById, this.props.channelMappingsById);
    this.setState({ modifiedChannelMappingsById: {} });
  };

  unstageChannelMappingChanges = (selectedChannelMappingIds, latestChannelMappingsById) => {
    this._suppressBlur = true;
    this.setState({ editingValues: {} }, () => { this._suppressBlur = false; });
    const { unstageChannelMappingChanges, channelMappingsById, originalChannelMappings } = this.props;

    // Also unstage any paired entries from key-field edits
    const allToUnstage = { ...selectedChannelMappingIds };
    for (const id of Object.keys(selectedChannelMappingIds)) {
      const cognosType = id.split('#')[0];
      if (!originalChannelMappings[id]) {
        // New entry: find paired silently-deleted old entry
        for (const existingId of Object.keys(channelMappingsById)) {
          const item = channelMappingsById[existingId];
          if (existingId.startsWith(cognosType + '#') && existingId !== id &&
              isNaN(item.version) && (item._deleted || (item.comments && item.comments.startsWith('Deleted by')))) {
            const original = originalChannelMappings[existingId];
            if (original && (!original.excluded || !original.ignored)) {
              allToUnstage[existingId] = item;
            }
          }
        }
      } else {
        // Old entry (staged as deleted): find paired new entry
        const item = channelMappingsById[id];
        if (isNaN(item.version) && (item._deleted || (item.comments && item.comments.startsWith('Deleted by')))) {
          for (const existingId of Object.keys(channelMappingsById)) {
            if (existingId.startsWith(cognosType + '#') && existingId !== id &&
                !originalChannelMappings[existingId]) {
              allToUnstage[existingId] = channelMappingsById[existingId];
            }
          }
        }
      }
    }

    unstageChannelMappingChanges(allToUnstage, latestChannelMappingsById);

    this.setState({
                    latestChannelMappings: [],
                    reviewChangesScreen: {
                      visible: this.state.reviewChangesScreen.visible,
                      stagedChannelMappings: this.getStagedChannelMappings(),
                      latestChannelMappingsById: channelMappingsById,
                      modifiedChannelMappingsById: {},
                    },
                  });
  };

  stageChannelMappingChanges = (selectedChannelMappingIds, latestChannelMappingsById) => {
    const { stageChannelMappingChanges, channelMappingsById } = this.props;

    stageChannelMappingChanges(selectedChannelMappingIds, latestChannelMappingsById);

    this.setState({
                    reviewChangesScreen: {
                      visible: this.state.reviewChangesScreen.visible,
                      stagedChannelMappings: this.getStagedChannelMappings(),
                      latestChannelMappingsById: channelMappingsById,
                      modifiedChannelMappingsById: {},
                    },
                  });
  };

  addChannelMappings = (selectedChannelMappingIds, latestChannelMappingsById) => {
    const { addChannelMappings, channelMappingsById } = this.props;

    addChannelMappings(selectedChannelMappingIds, latestChannelMappingsById);

    this.setState({
                    reviewChangesScreen: {
                      visible: this.state.reviewChangesScreen.visible,
                      stagedChannelMappings: this.getStagedChannelMappings(),
                      latestChannelMappingsById: channelMappingsById,
                      modifiedChannelMappingsById: {},
                    },
                  });
  };

  publishChannelMappingChanges = (stagedChannelMappings, latestChannelMappingsById) => {
    const { publishChannelMappingChanges, channelMappingsById } = this.props;

    publishChannelMappingChanges(stagedChannelMappings, latestChannelMappingsById);

    // Remove published items from the review modal
    const remainingStaged = { ...this.state.reviewChangesScreen.stagedChannelMappings };
    Object.keys(stagedChannelMappings).forEach(id => delete remainingStaged[id]);

    this.setState({
                    reviewChangesScreen: {
                      visible: this.state.reviewChangesScreen.visible,
                      stagedChannelMappings: remainingStaged,
                      latestChannelMappingsById: channelMappingsById,
                      latestChannelMappings: Object.values(channelMappingsById),
                      modifiedChannelMappingsById: {},
                    },
                  });
  };

  tableContentSelectorOptions = (columnDefinitions) => {
    return [
      {
        options: columnDefinitions.map((entry) => {
          return {
            id: entry.id,
            label: entry.columnName,
            visible: !hiddenColumns.includes(entry.id),
          };
        }),
        label: '',
      },
    ];
  };

  onAddChannelMapping = () => {
    this.props.refreshChannelMapping();
    this.setState({
                    addMappingScreen: {
                      visible: true,
                      stagedChannelMappings: this.getStagedChannelMappings(),
                      latestChannelMappingsById: this.props.channelMappingsById,
                      comment: 'Changes by user: ' + this.props.user + ' at ' + new Date().toUTCString(),
                      username: this.props.user,
                    },
                  });
  };

  onBulkUpload = () => {
    this.props.refreshChannelMapping();
    this.setState({ bulkUploadScreen: { visible: true } });
  };

  bulkUploadScreenDismiss = () => {
    this.setState({ bulkUploadScreen: { visible: false } });
    this.props.refreshChannelMapping();
  };

  render() {
    const { errorScreenMessage, successScreenMessage , channelMappings } = this.props;
    const columnDefinitions = this.getColumnDefinitions();
    const { reviewChangesScreen, addMappingScreen, bulkUploadScreen, latestChannelMappings } = this.state;

    const visibleMappings = channelMappings
      .filter(item => {
        const isDeleted = item._deleted || (item.comments && item.comments.startsWith('Deleted by'));
        if (isDeleted && !isNaN(item.version)) return false;
        return true;
      })
      .map(item => ({ ...item, _sortPriority: isNaN(item.version) ? 0 : 1 }));

    return (
      <div>
        <Table
          columnDefinitions={ columnDefinitions }
          items={ visibleMappings }
          header={
            channelMappings &&
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <h2>Channel Mappings <span className="awsui-util-header-counter">({ visibleMappings.length })</span></h2>
              </div>
              <div className="awsui-util-action-stripe-group">
                { !this.props.readOnly && (
                  <Button
                    text="Delete Selected"
                    onClick={this.onDeleteSelected}
                    disabled={!this.state.selectedItems.length}
                    icon="close"
                  />
                )}
                { !this.props.readOnly && (
                  <Button
                    text="Review/Publish"
                    variant="primary"
                    onClick={this.onClickReviewPublishChanges}
                    disabled={!this.hasStagedChanges()}
                  />
                )}
                { !this.props.readOnly && (
                  <Button
                    text="Add"
                    onClick={this.onAddChannelMapping}
                    icon="add-plus"
                    disabled={ !channelMappings.length }
                  />
                )}
                { !this.props.readOnly && (
                  <Button
                    text="Bulk Upload"
                    onClick={this.onBulkUpload}
                    icon="upload"
                    disabled={ !channelMappings.length }
                  />
                )}
                <Button
                  text="Download Data"
                  onClick={ () => this.onClickDownloadData(columnDefinitions, visibleMappings) }
                  icon="download"
                  disabled={ !channelMappings.length }
                />
              </div>
            </div>
          }
          loadingText={ 'Loading channel mappings...' }
          loading={ channelMappings && !channelMappings.length }
          stickyHeader
        >
        <TableSelection
          onSelectionChange={this.onSelectionChange}
          selectedItems={this.state.selectedItems}
          trackBy="id"
        />
        <TablePropertyFiltering
          filteringOptions={getChannelMappingFilters(channelMappings)}
          {...V2_PROPERTY_FILTERING_TEXT}
          placeholder="Filter channel mappings"
          filteringFunction={v2PropertyFilteringFunction}
          tokens={this.state.filteringTokens}
          operation={this.state.filteringOperation}
          onPropertyFilteringChange={(event) =>
            this.setState({ filteringTokens: event.detail.tokens, filteringOperation: event.detail.operation })
          }
        />
        <TableSorting
          sortingColumn="_sortPriority"
          sortableColumns={ [
            {id: '_sortPriority', field: '_sortPriority'},
            {id: 'cognosChannelType', field: 'cognosChannelType'},
            {id: 'cognosChannel', field: 'cognosChannel'},
            {id: 'groupCode', field: 'groupCode'},
            {id: 'excluded', field: 'excluded'},
            {id: 'hcpExcluded', field: 'hcpExcluded'},
            {id: 'hcpChannelType', field: 'hcpChannelType'},
            {id: 'hcpMerchantChannel', field: 'hcpMerchantChannel'},
            {id: 'marketplaceId', field: 'marketplaceId'},
            {id: 'derivedMarketplaceId', field: 'derivedMarketplaceId'},
            {id: 'marketplaceName', field: 'marketplaceName'},
          ] }
        />
        <TablePreferences title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
          <TableContentSelector
            title="Select visible columns"
            options={ this.tableContentSelectorOptions(columnDefinitions) }
          />
        </TablePreferences>
        <TablePagination pageSize={ tablePageSize } />
        </Table>
        <CMReviewChanges
          {...reviewChangesScreen}
          modifiedChannelMappingsById={this.state.modifiedChannelMappingsById}
          originalChannelMappings={this.props.originalChannelMappings}
          stageChannelMappingChanges={this.stageChannelMappingChanges}
          unstageChannelMappingChanges={this.unstageChannelMappingChanges}
          publishChannelMappingChanges={this.publishChannelMappingChanges}
          addChannelMappings={this.props.addChannelMappings}
          dropdownOptions={this.props.dropdownOptions}
          onDismiss={this.reviewChangesScreenDismiss}
          errorScreenMessage={errorScreenMessage}
          successScreenMessage={successScreenMessage}
        />
        <CMAddMapping
          {...addMappingScreen}
          addChannelMappings={this.addChannelMappings}
          originalChannelMappings={this.state.channelMappings}
          onDismiss={this.addMappingScreenDismiss}
          dropdownOptions={this.props.dropdownOptions}
          hcpChannelOptions={this.props.hcpChannelOptions}
          errorScreenMessage={errorScreenMessage}
          successScreenMessage={successScreenMessage}
          marketplaceConfig={this.props.marketplaceConfig}
        />
        <CMBulkUpload
          visible={bulkUploadScreen.visible}
          channelMappingsById={this.props.channelMappingsById}
          addChannelMappings={this.props.addChannelMappings}
          stageChannelMappingChanges={this.props.stageChannelMappingChanges}
          dropdownOptions={this.props.dropdownOptions}
          marketplaceConfig={this.props.marketplaceConfig}
          user={this.props.user}
          onDismiss={this.bulkUploadScreenDismiss}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/channel_mapping/channel_mapping_table.jsx